import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { es } from './locales/es';
import { ca } from './locales/ca';
import { en } from './locales/en';

import { messaging } from './locales/messaging';

const dictionaries = { es: { ...es, ...messaging.es }, ca: { ...ca, ...messaging.ca }, en: { ...en, ...messaging.en } };
const STORAGE_KEY = 'stagecrew.locale';

const I18nContext = createContext(null);

const getInitialLocale = () => {
  if (typeof window === 'undefined') return 'es';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored && dictionaries[stored] ? stored : 'es';
};

export const I18nProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(getInitialLocale);

  const setLocale = useCallback((next) => {
    if (!dictionaries[next]) return;
    setLocaleState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
  }, []);

  const t = useCallback((key) => {
    const dict = dictionaries[locale] || dictionaries.es;
    // Fallback to Spanish if key missing in current locale
    return dict[key] ?? dictionaries.es[key] ?? key;
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale, t, available: Object.keys(dictionaries) }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
};
